import React, { useState } from "react";
import { 
  Box, 
  Typography, 
  TextField, 
  List, 
  ListItem, 
  ListItemText, 
  Paper,
  InputAdornment,
  Divider,
  IconButton,
  CircularProgress
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import SendIcon from "@mui/icons-material/Send";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LaunchIcon from "@mui/icons-material/Launch";

// Import the real processed video data
import videoData from '../real_garret_smith_video.json';

const placeholderVideos = [
  { 
    id: 1, 
    title: videoData.videoInfo.title,
    duration: `${Math.floor(videoData.videoInfo.duration / 60)}:${(videoData.videoInfo.duration % 60).toString().padStart(2, '0')}`,
    videoId: videoData.videoId,
    videoUrl: videoData.videoUrl,
    chunks: videoData.chunks,
    transcript: videoData.transcript
  }
];

// Firebase functions is imported from firebaseConfig

const LandingPage = () => {
  const [selectedVideo, setSelectedVideo] = useState(placeholderVideos[0]);
  const [searchTerm, setSearchTerm] = useState("");
  const [chatInput, setChatInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [chatMessages, setChatMessages] = useState([
    { 
      id: 1, 
      text: `Hi! I'm here to help you understand Dr. Garret Smith's insights from "${selectedVideo.title}". What would you like to know?`, 
      isBot: true 
    }
  ]);

  const filteredVideos = placeholderVideos.filter(video =>
    video.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Convert timestamp string to seconds for YouTube URL
  const convertTimestampToSeconds = (timestamp) => {
    // Handle formats like "38s-65s", "2:30", "38s", etc.
    // Take the first timestamp if it's a range
    let timeStr = timestamp.split('-')[0];
    
    // Remove 's' suffix if present
    timeStr = timeStr.replace('s', '');
    
    // Handle mm:ss format
    if (timeStr.includes(':')) {
      const [minutes, seconds] = timeStr.split(':').map(Number);
      return (minutes * 60) + seconds;
    }
    
    // Handle seconds only
    return parseInt(timeStr) || 0;
  };

  // Call local chat server
  const callChatFunction = async (query) => {
    try {
      const response = await fetch('http://localhost:3003/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ query })
      });
      
      const result = await response.json();
      return result;
    } catch (error) {
      console.error("Error calling chat server:", error);
      return {
        success: false,
        error: error.message
      };
    }
  };

  // Handle sending a chat message
  const handleSendMessage = async () => {
    if (!chatInput.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      text: chatInput,
      isBot: false
    };

    setChatMessages(prev => [...prev, userMessage]);
    const currentQuery = chatInput;
    setChatInput("");
    setIsLoading(true);

    try {
      // Call Firebase Function
      const result = await callChatFunction(currentQuery);
      
      if (result.success) {
        const botMessage = {
          id: Date.now() + 1,
          text: result.response,
          isBot: true,
          relevantChunks: result.relevantChunks
        };
        setChatMessages(prev => [...prev, botMessage]);
      } else {
        const errorMessage = {
          id: Date.now() + 1,
          text: `Sorry, I encountered an error: ${result.error}`,
          isBot: true
        };
        setChatMessages(prev => [...prev, errorMessage]);
      }
    } catch (error) {
      const errorMessage = {
        id: Date.now() + 1,
        text: "I'm sorry, I encountered an error. Please try again.",
        isBot: true
      };
      setChatMessages(prev => [...prev, errorMessage]);
    }

    setIsLoading(false);
  };

  // Parse message text and render timestamps and quotes beautifully
  const renderMessageWithTimestamps = (text) => {
    // First handle italics (quotes), then timestamps
    const parts = [];
    let remainingText = text;
    
    // Split by italics first *"quoted text"*
    const italicRegex = /\*"([^"]*?)"\*/g;
    let lastIndex = 0;
    let match;
    
    while ((match = italicRegex.exec(text)) !== null) {
      // Add text before the italic
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          content: text.slice(lastIndex, match.index)
        });
      }
      
      // Add the italic quote
      parts.push({
        type: 'quote',
        content: match[1] // The quoted text without the *" "*
      });
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text
    if (lastIndex < text.length) {
      parts.push({
        type: 'text',
        content: text.slice(lastIndex)
      });
    }
    
    // If no italics found, treat whole text as regular text
    if (parts.length === 0) {
      parts.push({
        type: 'text',
        content: text
      });
    }
    
    // Now render each part, handling timestamps within text parts
    return parts.map((part, partIndex) => {
      if (part.type === 'quote') {
        return (
          <Box
            key={partIndex}
            component="span"
            sx={{
              fontStyle: "italic",
              bgcolor: "#f0f8ff",
              color: "#1565c0",
              px: 1,
              py: 0.2,
              borderRadius: 1,
              border: "1px solid #e3f2fd",
              mx: 0.5
            }}
          >
            "{part.content}"
          </Box>
        );
      }
      
      // Handle timestamps in regular text
      const timestampRegex = /\[(\d+(?::\d+)?(?:s)?(?:-\d+(?::\d+)?(?:s)?)?)\]/g;
      const textParts = part.content.split(timestampRegex);
      
      return textParts.map((textPart, textIndex) => {
        // Check if this part is a timestamp
        if (timestampRegex.test(`[${textPart}]`)) {
          return (
            <Box
              key={`${partIndex}-${textIndex}`}
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                mx: 0.5,
                px: 1,
                py: 0.3,
                bgcolor: "#e8f5e8",
                color: "#2e7d32",
                borderRadius: 3,
                fontSize: "0.85rem",
                fontWeight: 600,
                border: "1px solid #4caf50",
                cursor: "pointer",
                transition: "all 0.2s ease",
                "&:hover": {
                  bgcolor: "#4caf50",
                  color: "white",
                  transform: "scale(1.05)"
                }
              }}
              onClick={() => {
                // Convert timestamp to seconds and open YouTube at that time
                const timeInSeconds = convertTimestampToSeconds(textPart);
                const youtubeUrl = `${selectedVideo.videoUrl}&t=${timeInSeconds}s`;
                window.open(youtubeUrl, '_blank');
              }}
            >
              <AccessTimeIcon sx={{ fontSize: "0.8rem" }} />
              {textPart}
            </Box>
          );
        }
        // Regular text
        return <span key={`${partIndex}-${textIndex}`}>{textPart}</span>;
      });
    });
  };

  const handleVideoSelect = (video) => {
    setSelectedVideo(video);
    setChatMessages([
      { 
        id: Date.now(), 
        text: `Hi! I'm here to help you understand Dr. Garret Smith's insights from "${video.title}". What would you like to know?`, 
        isBot: true 
      }
    ]);
  };

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Box 
        sx={{ 
          background: "linear-gradient(135deg, #2e7d32 0%, #4caf50 100%)",
          color: "white",
          p: 2,
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Chat with Dr. Garret Smith's "Love Your Liver" Podcasts
        </Typography>
        <Typography variant="subtitle2" sx={{ opacity: 0.9, mt: 0.5 }}>
          Get instant insights from his liver health expertise
        </Typography>
      </Box>

      {/* Main Content */}
      <Box sx={{ display: "flex", flex: 1, overflow: "hidden" }}>
        {/* Left Sidebar - Video List */}
        <Box 
          sx={{ 
            width: 400, 
            borderRight: "1px solid #e0e0e0",
            display: "flex",
            flexDirection: "column",
            bgcolor: "#fafafa"
          }}
        >
          {/* Search */}
          <Box sx={{ p: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search episodes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Video List */}
          <Box sx={{ flex: 1, overflow: "auto" }}>
            <List sx={{ py: 0 }}>
              {filteredVideos.map((video) => (
                <React.Fragment key={video.id}>
                  <ListItem
                    selected={selectedVideo.id === video.id}
                    sx={{
                      py: 2,
                      "&.Mui-selected": {
                        bgcolor: "#e8f5e8",
                        borderRight: "3px solid #4caf50"
                      },
                      "&:hover": {
                        bgcolor: "#f5f5f5"
                      },
                      cursor: "pointer",
                      position: "relative"
                    }}
                  >
                    {/* Main clickable area for chat selection */}
                    <Box
                      onClick={() => handleVideoSelect(video)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        cursor: "pointer"
                      }}
                    >
                      <PlayCircleIcon sx={{ mr: 2, color: "#4caf50" }} />
                      <ListItemText
                        primary={video.title}
                        secondary={`Duration: ${video.duration}`}
                        primaryTypographyProps={{
                          fontSize: "0.9rem",
                          fontWeight: selectedVideo.id === video.id ? 600 : 400
                        }}
                        secondaryTypographyProps={{
                          fontSize: "0.8rem"
                        }}
                      />
                    </Box>
                    
                    {/* YouTube link button */}
                    <Box
                      component="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(video.videoUrl, '_blank');
                      }}
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.3,
                        px: 0.8,
                        py: 0.4,
                        bgcolor: "#ff0000",
                        color: "white",
                        border: "none",
                        borderRadius: 1,
                        cursor: "pointer",
                        fontSize: "0.7rem",
                        fontWeight: 500,
                        transition: "all 0.2s ease",
                        opacity: 0.8,
                        "&:hover": {
                          opacity: 1,
                          transform: "scale(1.05)",
                          boxShadow: "0 2px 4px rgba(255,0,0,0.3)"
                        }
                      }}
                    >
                      <LaunchIcon sx={{ fontSize: "0.7rem" }} />
                      YT
                    </Box>
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
          </Box>
        </Box>

        {/* Right Side - Chat Interface */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          {/* Chat Header */}
          <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0", bgcolor: "white" }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 500, mb: 0.5 }}>
                  {selectedVideo.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Duration: {selectedVideo.duration}
                </Typography>
              </Box>
              
              {/* YouTube link button */}
              <Box
                component="button"
                onClick={() => window.open(selectedVideo.videoUrl, '_blank')}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 1.5,
                  py: 0.8,
                  bgcolor: "#ff0000",
                  color: "white",
                  border: "none",
                  borderRadius: 2,
                  cursor: "pointer",
                  fontSize: "0.8rem",
                  fontWeight: 500,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: "#d32f2f",
                    transform: "translateY(-1px)",
                    boxShadow: "0 4px 8px rgba(255,0,0,0.3)"
                  }
                }}
              >
                <PlayCircleIcon sx={{ fontSize: "1rem" }} />
                Watch on YouTube
                <LaunchIcon sx={{ fontSize: "0.9rem" }} />
              </Box>
            </Box>
          </Box>

          {/* Chat Messages */}
          <Box sx={{ flex: 1, p: 2, overflow: "auto", bgcolor: "#f8f9fa" }}>
            {chatMessages.map((message) => (
              <Box
                key={message.id}
                sx={{
                  display: "flex",
                  justifyContent: message.isBot ? "flex-start" : "flex-end",
                  mb: 3
                }}
              >
                <Paper
                  sx={{
                    p: 2.5,
                    maxWidth: "80%",
                    bgcolor: message.isBot ? "white" : "#4caf50",
                    color: message.isBot ? "text.primary" : "white",
                    boxShadow: message.isBot 
                      ? "0 2px 8px rgba(0,0,0,0.1)" 
                      : "0 2px 8px rgba(76, 175, 80, 0.3)",
                    borderRadius: message.isBot ? "20px 20px 20px 4px" : "20px 20px 4px 20px",
                    position: "relative",
                    "&::before": message.isBot ? {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: -8,
                      width: 0,
                      height: 0,
                      borderTop: "8px solid white",
                      borderLeft: "8px solid transparent",
                      borderRight: "8px solid transparent",
                    } : {}
                  }}
                >
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      lineHeight: 1.6,
                      fontSize: "0.95rem"
                    }}
                  >
                    {message.isBot ? renderMessageWithTimestamps(message.text) : message.text}
                  </Typography>
                  
                  {/* Show relevant chunks info for bot messages */}
                  {message.isBot && message.relevantChunks && message.relevantChunks.length > 0 && (
                    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: "1px solid #f0f0f0" }}>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: "#666",
                          fontSize: "0.75rem",
                          fontStyle: "italic"
                        }}
                      >
                        💡 Based on {message.relevantChunks.length} relevant segments from the video
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Box>
            ))}
            
            {/* Loading indicator */}
            {isLoading && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  mb: 2
                }}
              >
                <Paper
                  sx={{
                    p: 2,
                    bgcolor: "white",
                    borderRadius: "20px 20px 20px 4px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">
                      Thinking...
                    </Typography>
                  </Box>
                </Paper>
              </Box>
            )}
          </Box>

          {/* Chat Input */}
          <Box sx={{ p: 2, borderTop: "1px solid #e0e0e0", bgcolor: "white" }}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                fullWidth
                placeholder={`Ask about "${selectedVideo.title}"...`}
                variant="outlined"
                size="small"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                disabled={isLoading}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2
                  }
                }}
              />
              <IconButton 
                onClick={handleSendMessage}
                disabled={!chatInput.trim() || isLoading}
                sx={{ 
                  bgcolor: "#4caf50", 
                  color: "white",
                  "&:hover": { bgcolor: "#45a049" },
                  "&:disabled": { bgcolor: "#ccc" }
                }}
              >
                {isLoading ? <CircularProgress size={20} /> : <SendIcon />}
              </IconButton>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LandingPage;